import { useState } from 'react';

const USER_HAS_CLOSED_KEY = 'rocket:hasClosed';
const TEMP_OPEN_TIMEOUT = 3_000;

const getClosedState = () => {
  try {
    return localStorage.getItem(USER_HAS_CLOSED_KEY) === 'true';
  } catch {
    // localStorage might error out with some privacy settings. Just ignore it if so.
    return false;
  }
};

// If the user has manually closed the Rocket panel, we store that state in localStorage
const storeClosedState = (hasClosed: boolean | string) => {
  try {
    localStorage.setItem(USER_HAS_CLOSED_KEY, hasClosed.toString());
  } catch {
    // localStorage might error out with some privacy settings. Just ignore it if so.
  }
};

export const useOpenState = () => {
  const [isPermanentlyOpen, setIsPermanentlyOpen] = useState(false);
  const [isTemporarilyOpen, setIsTemporarilyOpen] = useState(false);

  const isOpen = isPermanentlyOpen || isTemporarilyOpen;

  const hasUserManuallyClosed = getClosedState();

  const openTemporarily = () => {
    if (isPermanentlyOpen) {
      return () => {
        // Would return a cleanup function, but not needed here,
        // so we return a no-op function for ergonomics.
      };
    }

    setIsTemporarilyOpen(true);
    const timeout = setTimeout(() => {
      setIsTemporarilyOpen(false);
    }, TEMP_OPEN_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
  };

  const close = () => {
    setIsPermanentlyOpen(false);
    setIsTemporarilyOpen(false);
    storeClosedState(true);
  };

  const open = () => {
    setIsPermanentlyOpen(true);
    storeClosedState(false);
  };

  return { isOpen, isPermanentlyOpen, openTemporarily, close, open, hasUserManuallyClosed };
};
